import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../auth/UserContext.js";
import { Icon } from "../../components/Icon.js";
import { Callout } from "../../components/Callout.js";
import { ProjectionChart } from "../../components/ProjectionChart.js";
import { fmt } from "../../lib/money.js";
import { monthsLabel, monthsToReach } from "../../lib/projection.js";
import { deleteGoal, depositToGoal, patchGoal } from "../../api/goals.js";
import type { KidBundle } from "../../api/me.js";

export default function GoalDetail() {
  const { bundle, refresh } = useUser();
  const navigate = useNavigate();
  const { goalId } = useParams<{ goalId: string }>();

  const [editAlloc, setEditAlloc] = useState(false);
  const [newAllocType, setNewAllocType] = useState<"$" | "%">("$");
  const [newAllocValue, setNewAllocValue] = useState<string>("0");
  const [depositAmt, setDepositAmt] = useState("");
  const [busy, setBusy] = useState(false);

  if (!bundle || bundle.user.role !== "kid") return null;
  const kid = bundle as KidBundle;
  const goal = kid.goals.find((g) => g.rowKey === goalId);

  const allocatedToGoals = kid.goals.reduce((s, g) => s + g.savedCents, 0);
  const unallocated = kid.account.balanceCents - allocatedToGoals;

  const annualRate = kid.settings.interestRateAnnualBasisPoints / 10000;
  const monthlyRate = annualRate / 12;
  const monthlyIncome = kid.account.allowanceCents + kid.account.otherIncomeCents;
  const color = kid.user.color ?? "var(--brand)";
  const colorSoft = kid.user.colorSoft ?? "var(--brand-soft)";

  const monthlyContribution = useMemo(() => {
    if (!goal) return 0;
    return goal.allocType === "$" ? goal.allocValue : (monthlyIncome * goal.allocValue) / 100;
  }, [goal, monthlyIncome]);

  const monthsNoInterest = goal ? monthsToReach(goal, monthlyContribution, 0) : 0;
  const monthsWithInterest = goal ? monthsToReach(goal, monthlyContribution, monthlyRate) : 0;

  const projection = useMemo(() => {
    if (!goal) return [] as number[];
    const arr: number[] = [];
    let saved = goal.savedCents;
    const max = Math.min(monthsNoInterest === Infinity ? 36 : monthsNoInterest, 36);
    for (let i = 0; i <= max; i++) {
      arr.push(saved);
      saved = saved * (1 + monthlyRate) + monthlyContribution;
    }
    return arr;
  }, [goal, monthlyRate, monthlyContribution, monthsNoInterest]);

  if (!goal) {
    return (
      <div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate("/kid/goals")}
          style={{ marginBottom: 18 }}
        >
          <Icon name="chevronLeft" size={14} /> Back to goals
        </button>
        <div className="card">Goal not found.</div>
      </div>
    );
  }

  const pct = Math.min(100, (goal.savedCents / goal.targetCents) * 100);

  const startEditAlloc = () => {
    setNewAllocType(goal.allocType);
    setNewAllocValue(
      goal.allocType === "$" ? (goal.allocValue / 100).toString() : goal.allocValue.toString(),
    );
    setEditAlloc(true);
  };

  const saveAlloc = async () => {
    const num = Number(newAllocValue);
    if (Number.isNaN(num)) return;
    const value = newAllocType === "$" ? Math.round(num * 100) : Math.round(num);
    setBusy(true);
    try {
      await patchGoal(kid.user.rowKey, goal.rowKey, {
        allocType: newAllocType,
        allocValue: value,
      });
      await refresh();
      setEditAlloc(false);
    } finally {
      setBusy(false);
    }
  };

  const onDeposit = async () => {
    const v = Number(depositAmt);
    if (!v || Number.isNaN(v)) return;
    setBusy(true);
    try {
      await depositToGoal(kid.user.rowKey, goal.rowKey, Math.round(v * 100));
      await refresh();
      setDepositAmt("");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await deleteGoal(kid.user.rowKey, goal.rowKey);
      await refresh();
      navigate("/kid/goals");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate("/kid/goals")}
        style={{ marginBottom: 18 }}
      >
        <Icon name="chevronLeft" size={14} /> Back to goals
      </button>

      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24 }}>
        <div
          className="card"
          style={{
            padding: 32,
            position: "relative",
            overflow: "hidden",
            background: `linear-gradient(135deg, ${colorSoft}, white 70%)`,
          }}
        >
          <div
            className="decoration"
            style={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: color,
              opacity: 0.1,
            }}
          />

          <div className="row" style={{ marginBottom: 20 }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 20,
                background: color,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name={goal.icon} size={36} />
            </div>
            <div>
              <h2 style={{ marginBottom: 4 }}>{goal.title}</h2>
              <div className="soft" style={{ fontSize: 14 }}>
                Target: {fmt(goal.targetCents, { showCents: false })}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14 }}>
            <div className="money money-xl">{fmt(goal.savedCents)}</div>
            <div className="soft">saved so far</div>
          </div>
          <div className="progress" style={{ height: 14, marginBottom: 10 }}>
            <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
          </div>
          <div className="row-between" style={{ fontSize: 13 }}>
            <div className="soft">{pct.toFixed(1)}% complete</div>
            <div className="soft">
              {fmt(goal.targetCents - goal.savedCents, { showCents: false })} to go
            </div>
          </div>
        </div>

        <div className="stack" style={{ gap: 16 }}>
          <div className="card">
            <div className="soft" style={{ fontSize: 13, marginBottom: 4 }}>
              Time to reach goal
            </div>
            <div className="money money-lg" style={{ marginBottom: 6 }}>
              {monthsLabel(monthsWithInterest)}
            </div>
            <Callout>
              <div style={{ fontSize: 13 }}>
                Without interest, it would take <b>{monthsLabel(monthsNoInterest)}</b>. The
                bank&apos;s interest helps you get there faster!
              </div>
            </Callout>
          </div>

          <div className="card">
            <div className="row-between" style={{ marginBottom: 10 }}>
              <div className="soft" style={{ fontSize: 13 }}>
                Saving each month
              </div>
              {!editAlloc && (
                <button className="btn btn-ghost btn-sm" onClick={startEditAlloc}>
                  Edit
                </button>
              )}
            </div>
            {!editAlloc ? (
              <>
                <div className="money money-md">{fmt(monthlyContribution)}</div>
                <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
                  {goal.allocType === "$"
                    ? "Fixed amount"
                    : `${goal.allocValue}% of your ${fmt(monthlyIncome)} monthly income`}
                </div>
              </>
            ) : (
              <div className="stack" style={{ gap: 10 }}>
                <div className="seg">
                  <button
                    className={newAllocType === "$" ? "active" : ""}
                    onClick={() => setNewAllocType("$")}
                  >
                    Fixed $
                  </button>
                  <button
                    className={newAllocType === "%" ? "active" : ""}
                    onClick={() => setNewAllocType("%")}
                  >
                    % income
                  </button>
                </div>
                <input
                  className="input"
                  type="number"
                  value={newAllocValue}
                  onChange={(e) => setNewAllocValue(e.target.value)}
                />
                <div className="row" style={{ justifyContent: "flex-end" }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditAlloc(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-brand btn-sm" onClick={saveAlloc} disabled={busy}>
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Projection chart */}
      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ marginBottom: 16 }}>
          Your path to {fmt(goal.targetCents, { showCents: false })}
        </h3>
        <ProjectionChart projection={projection} target={goal.targetCents} color={color} />
      </div>

      {/* Allocate more now */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="row-between" style={{ marginBottom: 14 }}>
          <div>
            <h3>Move money into this goal</h3>
            <div className="soft" style={{ fontSize: 13, marginTop: 4 }}>
              You have {fmt(unallocated)} that&apos;s not yet assigned to a goal.
            </div>
          </div>
        </div>
        <div className="row">
          <input
            className="input"
            type="number"
            value={depositAmt}
            onChange={(e) => setDepositAmt(e.target.value)}
            placeholder="0.00"
            style={{ maxWidth: 200 }}
          />
          <button className="btn btn-brand" onClick={onDeposit} disabled={busy}>
            Add to goal
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-ghost"
            onClick={onDelete}
            style={{ color: "var(--neg)" }}
            disabled={busy}
          >
            Delete goal
          </button>
        </div>
      </div>
    </div>
  );
}
